import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getVenueGroups, addVenueGroup, removeVenueGroup, searchCommunities } from 'api';
import CommunitiesView from './view';

const CommunitiesContainer = ({ venueData, onVenueUpdate }) => {
  const [venueGroups, setVenueGroups] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isRemoving, setIsRemoving] = useState(null); // Stores slug of group being removed
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Use ref to avoid dependency cycle with venueGroups
  const venueGroupsRef = useRef(venueGroups);
  venueGroupsRef.current = venueGroups;

  // Fetch groups associated with this venue
  const fetchVenueGroups = useCallback(async () => {
    if (!venueData?.slug) return;
    
    setIsLoading(true);
    try {
      const response = await getVenueGroups(venueData.slug);
      const groups = response?.groups || [];
      setVenueGroups(groups);
    } catch (error) {
      console.error('Error fetching venue groups:', error);
      showSnackbar('Error loading communities', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [venueData?.slug]);

  // Fetch available groups for adding - use ref to avoid infinite loop
  const fetchAvailableGroups = useCallback(async (query = '') => {
    try {
      const response = await searchCommunities({ search: query, limit: 50 });
      // Filter out groups already associated with this venue using ref
      const existingSlugs = venueGroupsRef.current.map(vg => vg.group?.slug);
      const available = (response?.groups || []).filter(
        group => !existingSlugs.includes(group.slug)
      );
      setAvailableGroups(available);
    } catch (error) {
      console.error('Error fetching available groups:', error);
    }
  }, []); // No dependency on venueGroups - use ref instead

  // Initial fetch
  useEffect(() => {
    fetchVenueGroups();
  }, [fetchVenueGroups]);

  // Search available groups when dialog is opened or search query changes
  useEffect(() => {
    if (showAddDialog) {
      fetchAvailableGroups(searchQuery);
    }
  }, [showAddDialog, searchQuery, fetchAvailableGroups]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleAddGroup = async (groupSlug) => {
    setIsAdding(true);
    try {
      await addVenueGroup(venueData.slug, groupSlug);
      showSnackbar('Community added successfully', 'success');
      await fetchVenueGroups();
      setShowAddDialog(false);
      setSearchQuery('');
      if (onVenueUpdate) onVenueUpdate();
    } catch (error) {
      console.error('Error adding group:', error);
      const message = error?.response?.data?.error || 'Error adding community';
      showSnackbar(message, 'error');
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveGroup = async (groupSlug) => {
    setIsRemoving(groupSlug);
    try {
      await removeVenueGroup(venueData.slug, groupSlug);
      showSnackbar('Community removed successfully', 'success');
      await fetchVenueGroups();
      if (onVenueUpdate) onVenueUpdate();
    } catch (error) {
      console.error('Error removing group:', error);
      const message = error?.response?.data?.error || 'Error removing community';
      showSnackbar(message, 'error');
    } finally {
      setIsRemoving(null);
    }
  };

  const handleOpenAddDialog = () => {
    setShowAddDialog(true);
    setSearchQuery('');
  };

  const handleCloseAddDialog = () => {
    setShowAddDialog(false);
    setSearchQuery('');
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Handle pricing success from dialog
  const handlePricingSuccess = (message) => {
    showSnackbar(message, 'success');
  };

  return (
    <CommunitiesView
      venueGroups={venueGroups}
      availableGroups={availableGroups}
      isLoading={isLoading}
      isAdding={isAdding}
      isRemoving={isRemoving}
      searchQuery={searchQuery}
      showAddDialog={showAddDialog}
      snackbar={snackbar}
      onAddGroup={handleAddGroup}
      onRemoveGroup={handleRemoveGroup}
      onOpenAddDialog={handleOpenAddDialog}
      onCloseAddDialog={handleCloseAddDialog}
      onSearchChange={handleSearchChange}
      onCloseSnackbar={handleCloseSnackbar}
      onPricingSuccess={handlePricingSuccess}
      venueSlug={venueData?.slug}
    />
  );
};

export default CommunitiesContainer;
